import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, ArrowUpRight, Heart, MessageCircle, GitCompare } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useCompare } from "../context/CompareContext";
import { useLang } from "../context/LanguageContext";
import { resolveImg, resolveImgSized } from "../lib/api";
import { track } from "../lib/analytics";
import { toast } from "sonner";
import Stars from "./Stars";

export const formatJOD = (n) => {
  if (n == null) return "";
  return `${Number(n).toFixed(2)} د.ا`;
};

export default function ProductCard({ product, index = 0, rating, highlightBadge }) {
  const { addItem } = useCart();
  const { has: inWishlist, toggle: toggleWishlist } = useWishlist();
  const { has: inCompare, toggle: toggleCompare, count: cmpCount, max: cmpMax } = useCompare();
  const { t, tBrand, lang } = useLang();

  // Bilingual product name — prefer Arabic if available when lang === "ar"
  const displayName = lang === "ar" && product.name_ar ? product.name_ar : product.name;

  const variants = product.variants || [];
  const hasVariants = variants.length > 0;
  // Pick lowest variant price (or fallback to product-level price)
  const minPriceVariant = hasVariants
    ? variants.reduce((min, v) => {
        const p = v.sale_price || v.price;
        const mp = min.sale_price || min.price;
        return p < mp ? v : min;
      }, variants[0])
    : null;
  const displayPrice = hasVariants
    ? (minPriceVariant.sale_price || minPriceVariant.price)
    : (product.sale_price || product.price);
  const displayFullPrice = hasVariants ? minPriceVariant.price : product.price;
  const onSale = hasVariants ? !!minPriceVariant.sale_price : !!product.sale_price;
  const pct = onSale ? Math.round(((displayFullPrice - displayPrice) / displayFullPrice) * 100) : 0;
  const totalStock = hasVariants
    ? variants.reduce((s, v) => s + (v.stock || 0), 0)
    : (product.stock || 0);
  const outOfStock = totalStock <= 0;
  const quoteOnly = !!product.quote_only;

  const waUrl = `https://wa.me/962799448817?text=${encodeURIComponent(
    quoteOnly
      ? `Hi Shamstrade, I'd like a quote for: ${product.name}`
      : `Hi Shamstrade, I'd like to order: ${product.name} starting at ${Number(displayPrice).toFixed(2)} JOD`
  )}`;

  const handleWishlist = async () => {
    try {
      await toggleWishlist(product);
      toast.success(inWishlist(product.id) ? "Removed from wishlist" : "Added to wishlist");
    } catch (e) {
      toast.error(e.message || "Sign in to save");
    }
  };

  const handleCompare = () => {
    if (!inCompare(product.id) && cmpCount >= cmpMax) {
      toast.error(`Compare limit is ${cmpMax}`);
      return;
    }
    toggleCompare(product.id);
    toast.success(inCompare(product.id) ? "Removed from compare" : "Added to compare");
  };

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group relative bg-white transition-all duration-500 ease-out"
      style={{ animation: `fade-up 0.5s ${index * 60}ms cubic-bezier(0.16,1,0.3,1) backwards` }}
    >
      <Link
        to={`/product/${product.slug}`}
        onClick={() => track("product_click", { slug: product.slug, name: product.name })}
        className="block aspect-[4/5] overflow-hidden rounded-2xl bg-white shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_-15px_rgba(0,85,255,0.25)] transition-shadow duration-500 relative"
      >
        {product.images?.[0] && (
          <img
            src={resolveImgSized(product.images[0], 600)}
            alt={displayName}
            className="absolute inset-0 w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
            loading="lazy"
            decoding="async"
          />
        )}

        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />

        {/* Top-left badges */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
          {onSale && (
            <span className="inline-flex items-center gap-1.5 backdrop-blur-md bg-electric/90 text-white text-[11px] font-mono font-semibold px-2.5 py-1 rounded-full tracking-wider">
              <span className="w-1 h-1 rounded-full bg-white" />
              SAVE {pct}%
            </span>
          )}
          {outOfStock && (
            <span className="backdrop-blur-md bg-ink/90 text-white text-[10px] font-mono px-2.5 py-1 rounded-full uppercase tracking-wider">
              {t("common.outOfStock")}
            </span>
          )}
          {highlightBadge && (
            <span
              data-testid="product-card-highlight"
              className="backdrop-blur-md bg-ink text-electric text-[10px] font-mono font-bold px-2.5 py-1 rounded-full uppercase tracking-[0.15em]"
            >
              ✓ {highlightBadge}
            </span>
          )}
        </div>

        {/* Top-right: brand chip + wishlist */}
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
          <span className="backdrop-blur-md bg-white/70 text-ink text-[10px] font-mono font-semibold px-2.5 py-1 rounded-full uppercase tracking-[0.18em]">
            {tBrand(product.brand)}
          </span>
        </div>

        {/* Hover: arrow */}
        <span className="absolute bottom-3 right-3 z-10 w-10 h-10 rounded-full bg-white text-ink grid place-items-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 shadow-lg pointer-events-none">
          <ArrowUpRight size={16} />
        </span>
      </Link>

      <div className="pt-4 px-1">
        <Link to={`/product/${product.slug}`} onClick={() => track("product_click", { slug: product.slug, name: product.name })} className="block">
          <h3 className="font-display font-extrabold text-lg leading-tight text-ink group-hover:text-electric transition-colors duration-300">
            {displayName}
          </h3>
        </Link>

        <div className="mt-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            {hasVariants ? (
              // Surface every variant's size/BTU as chips so customers see all
              // available options at a glance (e.g. 48000 + 60000 BTU on the
              // RECTO floor-standing). Avoids hiding an entire SKU behind one
              // legacy specs.btu value.
              variants.map((v) => (
                <span
                  key={v.id}
                  data-testid={`variant-chip-${v.id}`}
                  className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-secondary text-slate-700"
                >
                  {v.btu ? `${v.btu} BTU` : v.label}
                </span>
              ))
            ) : (
              <>
                {product.specs?.btu && (
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                    {product.specs.btu} BTU
                  </span>
                )}
                {product.specs?.energy_rating && (
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">
                    · {product.specs.energy_rating}
                  </span>
                )}
              </>
            )}
          </div>
          {rating && rating.count > 0 && <Stars value={rating.avg} count={rating.count} size={12} />}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-baseline gap-2 flex-wrap min-w-0">
            {quoteOnly ? (
              <span
                data-testid={`quote-only-${product.slug}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-electric/10 text-electric text-[11px] font-mono uppercase tracking-widest"
              >
                <MessageCircle size={12} /> {t("common.priceOnRequest")}
              </span>
            ) : (
              <>
                {hasVariants && (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("common.from")}</span>
                )}
                <span className="font-display font-black text-xl text-ink">{formatJOD(displayPrice)}</span>
                {onSale && (
                  <span className="font-mono text-slate-400 line-through text-xs">
                    {formatJOD(displayFullPrice)}
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={handleWishlist}
              data-testid={`wishlist-${product.slug}`}
              className={`w-9 h-9 rounded-full grid place-items-center transition-all duration-300 ${
                inWishlist(product.id)
                  ? "bg-rose-500 text-white"
                  : "bg-secondary text-ink hover:bg-rose-500 hover:text-white"
              }`}
              aria-label="Toggle wishlist"
            >
              <Heart size={14} fill={inWishlist(product.id) ? "currentColor" : "none"} />
            </button>
            <button
              type="button"
              onClick={handleCompare}
              data-testid={`compare-${product.slug}`}
              className={`hidden sm:grid w-9 h-9 rounded-full place-items-center transition-all duration-300 ${
                inCompare(product.id) ? "bg-electric text-white" : "bg-secondary text-ink hover:bg-ink hover:text-white"
              }`}
              aria-label="Toggle compare"
            >
              <GitCompare size={14} />
            </button>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`whatsapp-${product.slug}`}
              className="hidden sm:grid w-9 h-9 rounded-full bg-[#25D366] text-white place-items-center hover:scale-110 transition-transform duration-300"
              aria-label="Order on WhatsApp"
            >
              <MessageCircle size={14} />
            </a>
            {quoteOnly ? (
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`request-quote-${product.slug}`}
                className="px-2.5 h-9 rounded-full bg-[#25D366] text-white inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] hover:brightness-110 transition whitespace-nowrap"
                aria-label={t("common.requestQuote")}
              >
                <MessageCircle size={12} className="shrink-0" />
                <span className="hidden sm:inline">{t("common.requestQuote")}</span>
                <span className="sm:hidden">{t("common.quoteShort")}</span>
              </a>
            ) : hasVariants ? (
              <Link
                to={`/product/${product.slug}`}
                data-testid={`select-options-${product.slug}`}
                aria-label={t("common.selectSize")}
                className="px-3 h-10 rounded-full bg-ink text-white inline-flex items-center text-[11px] font-mono uppercase tracking-widest hover:bg-electric transition-colors whitespace-nowrap"
              >
                {t("common.selectSize")}
              </Link>
            ) : (
              <button
                type="button"
                disabled={outOfStock}
                data-testid={`add-to-cart-${product.slug}`}
                onClick={() => {
                  addItem(product);
                  toast.success(`${product.name} ${t("common.addedToCart")}`);
                }}
                className="w-10 h-10 rounded-full bg-ink text-white grid place-items-center hover:bg-electric hover:scale-110 transition-all duration-300 disabled:opacity-40 disabled:hover:scale-100"
                aria-label={t("common.addToCart")}
              >
                <ShoppingCart size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
